import React from 'react';
import PropTypes from 'prop-types';
import FormModel from '../../models/form';
import LoadingCard from '../../components/loadingCard';
import { StatusComponent } from '../rates/components/rateStatus';
import {
    WeezHeader,
    Table,
    Icon,
    Checkbox,
    Message,
    Dropdown,
    SelectButton,
    Modal,
    Button,
    Form,
    SubTitleCard,
    TitleCard,
    Card,
    ContentCard,
    SplitContainer,
    ToastContext
} from '@weezevent/nacre';
import { useHistory } from 'react-router-dom';
import { useSegment, useTrackingPage } from '../../components/tracking/trackingProvider';
import { useTranslation, Trans } from 'react-i18next';
import useAppContext from '../../context/useAppContext';
import { useFetchFields } from './details';

import Style from './style/list.module.css';
import { FormPreview } from '../forms/components/formPreview';
import { removeEmoji } from '../event_creation/utils';

function FormStatus({ form }) {
    const { t } = useTranslation();

    const data = React.useMemo(() => {
        const { rates } = form;
        if (rates.length) {
            return { title: t('ticket.common.active'), color: 'green', tooltip: t('ticket.form.common.rates.active_tooltip') };
        }
        return { title: t('ticket.common.inactive'), color: 'orange', tooltip: t('ticket.form.common.rates.inactive_tooltip') };
    }, [form]);

    return <StatusComponent {...data} />;
}
FormStatus.propTypes = {
    form: PropTypes.object.isRequired
};

function FormsTableHeader({ allChecked, onCheck }) {
    const { t } = useTranslation();
    return (
        <Table.Tr>
            <th className={Style['width-x-mini']}>
                <Checkbox checked={allChecked} onChange={onCheck} style={{ marginLeft: '5px' }} />
            </th>
            <th>{t('ticket.common.my_forms')}</th>
            <th>{t('ticket.common.rates')}</th>
            <th>{t('ticket.common.status')}</th>
            <th>{t('ticket.common.actions')}</th>
        </Table.Tr>
    );
}
FormsTableHeader.propTypes = {
    allChecked: PropTypes.bool.isRequired,
    onCheck: PropTypes.func.isRequired
};

function FormTableRowRates({ form }) {
    const { t } = useTranslation();
    return <span>{t('ticket.common.linked_ticket_count_interval', { postProcess: 'interval', count: form.rates?.length || 0 })}</span>;
}
FormTableRowRates.propTypes = {
    form: PropTypes.object.isRequired
};

function FormTableRowActions({ form, onDuplicate, onDelete, onPreview }) {
    const { t } = useTranslation();
    const { track } = useSegment();

    return (
        <div className={Style['action-container']}>
            <Icon
                name="eye"
                className={Style['eye']}
                onClick={() => {
                    onPreview(form);
                    track('ACTION > PREVIEW FORM', { 'Page Title': 'FORM', 'Action type': 'Unique' });
                }}
            />
            <Dropdown className={Style['dropdown']} onClick={() => track('ACTION > CELL ACTIONS', { 'Page Title': 'FORM' })}>
                <Dropdown.Item
                    onClick={() => {
                        onDuplicate(form);
                        track('ACTION > DUPLICATE FORM', { 'Page Title': 'FORM', 'Action type': 'Unique' });
                    }}
                >
                    {t('ticket.common.action.duplicate')}
                </Dropdown.Item>
                <Dropdown.Line />
                <Dropdown.Item
                    deleteItem
                    onClick={() => {
                        onDelete(form);
                        track('ACTION > DELETE FORM', { 'Page Title': 'FORM', 'Action type': 'Unique' });
                    }}
                >
                    {t('ticket.common.action.delete')}
                </Dropdown.Item>
            </Dropdown>
        </div>
    );
}

function FormsTableRow({ form, checked, onCheck, onDuplicate, onDelete, onPreview }) {
    const history = useHistory();
    const [{ event, organization }] = useAppContext();

    return (
        <Table.Tr active={checked}>
            <Table.Td>
                <Checkbox checked={checked} onChange={onCheck} style={{ marginLeft: '5px' }} />
            </Table.Td>
            <Table.Td className={Style['table-field']} onClick={() => history.push(`/ticket/O${organization.pk}/E${event.pk}/settings/forms/${form.pk}`)}>
                {form.name}
            </Table.Td>
            <Table.Td className={Style['table-field']} onClick={() => history.push(`/ticket/O${organization.pk}/E${event.pk}/settings/forms/${form.pk}`)}>
                <FormTableRowRates form={form} />
            </Table.Td>
            <Table.Td onClick={() => history.push(`/ticket/O${organization.pk}/E${event.pk}/settings/forms/${form.pk}`)}>
                <FormStatus form={form} />
            </Table.Td>
            <Table.Td>
                <FormTableRowActions form={form} onDuplicate={onDuplicate} onDelete={onDelete} onPreview={onPreview} />
            </Table.Td>
        </Table.Tr>
    );
}

function FormsTable({ forms, onFormCheck, selectedForms, onDuplicate, onDelete, onPreview }) {
    return (
        <Table useNew responsive style={{ marginBottom: '15px' }}>
            <Table.THead>
                <FormsTableHeader allChecked={selectedForms.length === forms.length} onCheck={(event, { checked }) => onFormCheck(checked, null, true)} />
            </Table.THead>
            <Table.TBody>
                {forms.map((form, i) => (
                    <FormsTableRow
                        key={i}
                        form={form}
                        checked={selectedForms.some(form_id => form_id === form.pk)}
                        onCheck={(event, { checked }) => onFormCheck(checked, form)}
                        onDuplicate={onDuplicate}
                        onDelete={onDelete}
                        onPreview={onPreview}
                    />
                ))}
            </Table.TBody>
        </Table>
    );
}

function DuplicationModal({ form, onSubmit, onClose }) {
    const [formName, setFormName] = React.useState(form.name);
    const [loading, setLoading] = React.useState(false);
    const { t } = useTranslation();

    const disabled = React.useMemo(() => {
        if (!formName || formName === form.name) {
            return true;
        }
        return false;
    }, [formName, form]);

    const handleSubmit = React.useCallback(() => {
        setLoading(true);
        onSubmit(formName, form, () => setLoading(false));
    }, [onSubmit, formName, form]);

    return (
        <Modal
            open={Boolean(form)}
            onClose={onClose}
            title={t('ticket.form.list.actions.duplicate.title', { name: form.name })}
            subtitle={t('ticket.form.list.actions.duplicate.body')}
            submitButton={<Button primary disabled={disabled} isLoading={loading} onClick={handleSubmit} label={t('ticket.common.action.save')} />}
            cancelButton={<Button onClick={onClose} disabled={loading} label={t('ticket.common.action.cancel')} />}
        >
            <div className={Style['duplication-modal']}>
                <Form.Field style={{ marginTop: '0' }}>
                    <Form.Label label={t('ticket.form.list.actions.duplicate.label')} />
                    <Form.Input value={formName} onChange={event => setFormName(removeEmoji(event.target.value))} placeholder={t('ticket.form.common.registration')} />
                </Form.Field>
            </div>
        </Modal>
    );
}
DuplicationModal.propTypes = {
    form: PropTypes.object.isRequired,
    open: PropTypes.bool.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired
};

function DeletionModal({ forms, onSubmit, onClose }) {
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(false);

    const handleSubmit = React.useCallback(() => {
        setLoading(true);
        onSubmit(() => setLoading(false));
    }, [onSubmit, forms]);

    return (
        <Modal
            open={Boolean(forms?.length)}
            onClose={onClose}
            title={t('ticket.form.list.actions.delete.title_interval', { postProcess: 'interval', count: forms.length })}
            submitButton={<Button primary isLoading={loading} onClick={handleSubmit} label={t('ticket.common.action.delete')} />}
            cancelButton={<Button disabled={loading} onClick={onClose} label={t('ticket.common.action.cancel')} />}
        >
            <Trans i18nKey="ticket.form.list.actions.delete.body_interval" tOptions={{ postProcess: 'interval' }} values={{ name: forms[0].name, count: forms.length }} />
        </Modal>
    );
}

function PreviewModal({ form, onSubmit, onClose }) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const [loading] = React.useState(false);

    const handleSubmit = React.useCallback(() => {
        onSubmit();
    }, [onSubmit, form]);

    return (
        <Modal
            open={form}
            onClose={onClose}
            title={t('ticket.form.preview.modale.title')}
            subtitle={!form?.weez_id ? t('ticket.form.preview.modale.subtitle') : null}
            // no submit button if form contains id because id = default form, weez_id = real form
            submitButton={
                form?.weez_id ? (
                    <Button
                        primary
                        isLoading={loading}
                        inverted
                        onClick={() => {
                            handleSubmit();
                            track('ACTION > EDIT FORM PREVIEW', { 'Page Title': 'FORM' });
                        }}
                        label={t('ticket.form.preview.modale.modify')}
                    />
                ) : null
            }
        >
            <FormPreview fields={form?.form_entries} withPadding={false} />
        </Modal>
    );
}

function NoFormTitle({ onPreview }) {
    const { t } = useTranslation();
    return (
        <div className={Style['header']}>
            <div className={Style['title-container']}>
                <TitleCard title={t('ticket.form.list.default.title')} />
                <SubTitleCard title={t('ticket.form.list.default.subtitle')} />
            </div>
            <div className={Style['preview']} onClick={onPreview}>
                <Icon name="eye" className={Style['eye-no-form']} />
                <span className={Style['eye-title-no-form']}>{t('ticket.common.action.preview')}</span>
            </div>
        </div>
    );
}

function NoFormContainer({ onPreview, defaultForm }) {
    return (
        <div className={Style['no_form']}>
            <Card.Header>
                <Card.Title title={<NoFormTitle onPreview={() => onPreview(defaultForm)} />} />
            </Card.Header>
            <Card.Content>
                <div style={{ display: 'flex', flexDirection: 'column' }} className={Style['body']}>
                    {defaultForm.form_entries.map(({ form_field }) => (
                        <div key={form_field.pk}>{form_field.translated_label}</div>
                    ))}
                </div>
            </Card.Content>
        </div>
    );
}

export default function ListForms({ organization, event }) {
    const [forms, loading, sync] = FormModel.useApiModel({
        initial: [],
        organizationId: organization.pk,
        eventId: event.pk,
        query: {
            sync: true
        }
    });
    const [selectedForms, setSelectedForms] = React.useState([]);
    const [formToDuplicate, setFormToDuplicate] = React.useState(null);
    const [formsToDelete, setFormsToDelete] = React.useState(null);
    const [formToPreview, setFormToPreview] = React.useState(null);
    const history = useHistory();
    const { t } = useTranslation();
    const { track } = useSegment();
    const toast = React.useContext(ToastContext);

    useTrackingPage('VIEW - FORM', { 'Page Title': 'FORM' });

    const [{ default_form }, loadingFields] = useFetchFields(event.pk, organization.pk);

    const handleSelectedForms = React.useCallback(
        (checked, form, allSelected = false) => {
            if (allSelected) {
                return setSelectedForms(checked ? [...forms.map(form => form.pk)] : []);
            }
            const newSelectedForms = [...selectedForms];
            if (!checked) {
                const formIndex = selectedForms.findIndex(f => f === form.pk);
                newSelectedForms.splice(formIndex, 1);
                return setSelectedForms(newSelectedForms);
            }
            newSelectedForms.push(form.pk);
            return setSelectedForms(newSelectedForms);
        },
        [selectedForms, forms]
    );

    const handleFormDuplication = React.useCallback(
        (formName, previousForm, onError) => {
            const newForm = FormModel.duplicate(previousForm);
            delete newForm.weez_id;
            newForm.rates = [];
            newForm.name = formName;
            newForm
                .save({
                    organizationId: organization.pk,
                    eventId: event.pk
                })
                .then(() => {
                    toast.success(t('ticket.form.list.actions.duplicate.success'));
                    setFormToDuplicate(null);
                    setSelectedForms([]);
                    sync();
                })
                .catch(() => {
                    toast.error(t('ticket.form.list.actions.duplicate.error'));
                    onError();
                });
        },
        [sync, organization, event, toast]
    );

    const handleFormDeletion = React.useCallback(
        onError => {
            if (!formsToDelete?.length) {
                return;
            }
            return FormModel.destroy(formsToDelete, {
                organizationId: organization.pk,
                eventId: event.pk
            })
                .then(() => {
                    toast.success(t('ticket.form.list.actions.delete.success'));
                    setFormsToDelete(null);
                    setSelectedForms([]);
                    sync();
                })
                .catch(() => {
                    toast.error(t('ticket.form.list.actions.delete.error'));
                    onError();
                });
        },
        [sync, organization, event, formsToDelete, toast]
    );

    return (
        <div className={Style['form-list-container']}>
            <WeezHeader
                title={t('ticket.form.list.title')}
                rightComponent={[
                    <Button
                        primary
                        key="top-cta-form"
                        icon={<Icon name={'plus'} margin={false} />}
                        label={t('ticket.form.list.create')}
                        onClick={() => {
                            history.push(`/ticket/O${organization.pk}/E${event.pk}/settings/forms/new`);
                            track('ACTION > CREATE FORM', { 'Page Title': 'FORM' });
                        }}
                    />
                ]}
            >
                {t('ticket.form.list.subtitle')}
            </WeezHeader>
            {formToDuplicate && <DuplicationModal form={formToDuplicate} open={true} onSubmit={handleFormDuplication} onClose={() => setFormToDuplicate(null)} />}
            {formsToDelete && <DeletionModal forms={formsToDelete} onSubmit={handleFormDeletion} onClose={() => setFormsToDelete(null)} />}
            {formToPreview && (
                <PreviewModal
                    form={formToPreview}
                    onSubmit={() => {
                        history.push(`/ticket/O${organization.pk}/E${event.pk}/settings/forms/${formToPreview.pk}`);
                    }}
                    onClose={() => {
                        setFormToPreview(null);
                    }}
                />
            )}
            <SplitContainer
                size="maxi"
                displayAdditional
                additional={
                    <Message type={'tips'} title={t('ticket.common.tips')} mini>
                        <div>{t(`ticket.form.list.tips.${forms.length ? 'forms' : 'no_forms'}`)}</div>
                    </Message>
                }
            >
                <Card>
                    <LoadingCard loading={loading || loadingFields}>
                        {forms.length ? (
                            <>
                                <ContentCard>
                                    <div className={Style['header-container']}>
                                        <SelectButton
                                            onOpen={() => track('ACTION > ACTION ON SELECTION', { 'Page Title': 'FORM' })}
                                            title={t('ticket.form.list.select_interval', { postProcess: 'interval', count: selectedForms.length })}
                                            tooltipTitle={t('ticket.form.list.select_tooltip')}
                                            objects={selectedForms}
                                        >
                                            <Dropdown.Item
                                                disabled={selectedForms?.length !== 1}
                                                onClick={() => {
                                                    history.push(`/ticket/O${organization.pk}/E${event.pk}/settings/forms/${selectedForms[0]}`);
                                                    track('ACTION > EDIT FORM', { 'Page Title': 'FORM', 'Action type': 'Multiple' });
                                                }}
                                            >
                                                {t('ticket.common.action.edit')}
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                                disabled={selectedForms?.length !== 1}
                                                onClick={() => {
                                                    setFormToPreview(...forms.filter(f => selectedForms.includes(f.pk)));
                                                    track('ACTION > PREVIEW FORM', { 'Page Title': 'FORM', 'Action type': 'Multiple' });
                                                }}
                                            >
                                                {t('ticket.common.action.preview')}
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                                disabled={selectedForms?.length !== 1}
                                                onClick={() => {
                                                    setFormToDuplicate(...forms.filter(f => selectedForms.includes(f.pk)));
                                                    track('ACTION > DUPLICATE FORM', { 'Page Title': 'FORM', 'Action type': 'Multiple' });
                                                }}
                                            >
                                                {t('ticket.common.action.duplicate')}
                                            </Dropdown.Item>
                                            <Dropdown.Line />
                                            <Dropdown.Item
                                                deleteItem
                                                onClick={() => {
                                                    setFormsToDelete([...forms.filter(f => selectedForms.includes(f.pk))]);
                                                    track('ACTION > DELETE FORM', { 'Page Title': 'FORM', 'Action type': 'Multiple' });
                                                }}
                                            >
                                                {t('ticket.common.action.delete')}
                                            </Dropdown.Item>
                                        </SelectButton>
                                        <div className={Style['total-form']}>{t('ticket.form.list.total_form', { count: forms.length })}</div>
                                    </div>
                                </ContentCard>
                                <FormsTable
                                    forms={forms}
                                    selectedForms={selectedForms}
                                    onFormCheck={handleSelectedForms}
                                    onDuplicate={form => setFormToDuplicate(form)}
                                    onDelete={form => setFormsToDelete([form])}
                                    onPreview={setFormToPreview}
                                />
                            </>
                        ) : (
                            <NoFormContainer onPreview={setFormToPreview} defaultForm={default_form} />
                        )}
                    </LoadingCard>
                </Card>
            </SplitContainer>
        </div>
    );
}

ListForms.propTypes = {
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired
};
